package com.arena_web.utils

import kotlinx.browser.localStorage
import org.w3c.dom.get
import org.w3c.dom.set

object Storage {
    private const val KEY_PREFIX = ""

    fun save(key: String, value: String) {
        try {
            localStorage["${KEY_PREFIX}$key"] = value
        } catch (e: Exception) {

            console.error("Failed to save to localStorage: $key", e)
        }
    }

    fun get(key: String): String? {
        return try {
            localStorage["${KEY_PREFIX}$key"]
        } catch (e: Exception) {
            console.error("Failed to read from localStorage: $key", e)
            null
        }
    }

    fun saveBoolean(key: String, value: Boolean) {
        save(key, value.toString())
    }

    fun getBoolean(key: String, defaultValue: Boolean = false): Boolean {
        return get(key)?.toBoolean() ?: defaultValue
    }

    fun saveInt(key: String, value: Int) {
        save(key, value.toString())
    }

    fun getInt(key: String, defaultValue: Int = 0): Int {
        return get(key)?.toIntOrNull() ?: defaultValue
    }

    fun remove(key: String) {
        try {
            localStorage.removeItem("${KEY_PREFIX}$key")
        } catch (e: Exception) {
            console.error("Failed to remove from localStorage: $key", e)
        }
    }

    fun clear() {
        try {
            val keysToRemove = mutableListOf<String>()
            for (i in 0 until localStorage.length) {
                val key = localStorage.key(i)
                if (key != null && key.startsWith(KEY_PREFIX)) {
                    keysToRemove.add(key)
                }
            }
            keysToRemove.forEach { localStorage.removeItem(it) }
        } catch (e: Exception) {
            console.error("Failed to clear localStorage", e)
        }
    }

    fun clearAuth() {
        AUTH_KEYS.forEach { remove(it) }
    }

    fun clearSession() {
        AUTH_KEYS.forEach { remove(it) }
        SESSION_KEYS.forEach { remove(it) }
    }

    fun contains(key: String): Boolean {
        return get(key) != null
    }

    private val SESSION_KEYS = listOf(
        StorageKeys.SELECTED_SPORT_ID,
        StorageKeys.SELECTED_SPORT_NAME,
        StorageKeys.CHART_GOALS_PERIOD,
        StorageKeys.CHART_ASSISTS_PERIOD,
        StorageKeys.ACTIVE_ROLE,
        StorageKeys.SELECTED_CLUB_ID,
        StorageKeys.SELECTED_CITY,
        StorageKeys.SELECTED_COMMUNITY_ID,
        StorageKeys.SELECTED_COMMUNITY_NAME,
        StorageKeys.ARENA_TAB
    )

    private val AUTH_KEYS = listOf(
        StorageKeys.ACCESS_TOKEN,
        StorageKeys.REFRESH_TOKEN,
        StorageKeys.USER_ID,
        StorageKeys.USER_EMAIL,
        StorageKeys.USERNAME,
        StorageKeys.FULL_NAME,
        StorageKeys.AVATAR_URL,
        StorageKeys.WALLET_BALANCE,
        StorageKeys.WALLET_CURRENCY,
        StorageKeys.WALLET_PENDING_ORDER,
        StorageKeys.WALLET_PENDING_COINS
    )
}

object StorageKeys {
    const val ACCESS_TOKEN = "access_token"
    const val REFRESH_TOKEN = "refresh_token"
    const val USER_ID = "user_id"
    const val USER_EMAIL = "user_email"
    const val USERNAME = "username"
    const val FULL_NAME = "full_name"
    const val AVATAR_URL = "avatar_url"
    const val LANGUAGE = "language"
    const val THEME = "theme"
    const val SELECTED_SPORT_ID = "selected_sport_id"
    const val SELECTED_SPORT_NAME = "selected_sport_name"
    const val CHART_GOALS_PERIOD = "chart_goals_period"
    const val CHART_ASSISTS_PERIOD = "chart_assists_period"
    const val WALLET_BALANCE = "wallet_balance"
    const val WALLET_CURRENCY = "wallet_currency"
    const val WALLET_PENDING_ORDER = "wallet_pending_order"
    const val WALLET_PENDING_COINS = "wallet_pending_coins"
    const val ACTIVE_ROLE = "active_role"
    const val SELECTED_CLUB_ID = "selected_club_id"
    const val SELECTED_CITY = "selected_city"
    const val SELECTED_COMMUNITY_ID = "selected_community_id"
    const val SELECTED_COMMUNITY_NAME = "selected_community_name"
    const val SELECTED_DATE = "arena_selected_date"
    const val ARENA_TAB = "arena_tab"
    const val GLOBAL_PALETTE = "global_palette"
    const val SPORTS_LIST_CACHE = "sports_list_cache"

    // ── Client analytics (AnalyticsTracker) ──
    const val ANALYTICS_DEVICE_ID = "analytics_device_id"
    const val ANALYTICS_SESSION_ID = "analytics_session_id"
    const val ANALYTICS_LAST_EVENT_AT = "analytics_last_event_at"
    const val ANALYTICS_QUEUE = "analytics_queue"
}
